import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, User, Building2, DollarSign, ChevronsUpDown, Check } from "lucide-react";
import { IndividualsRepository } from "@/repositories/individuals.repository";
import { cn } from "@/lib/utils";
import { EXTRA_PAYMENT_DESCRIPTION_MAX_LENGTH } from "@shyne/shared/api";

interface AssignableResource {
  id: string;
  name: string;
  type: "individual" | "sub";
}

interface ExtraPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    recipientType: "individual" | "subs";
    recipientId: string;
    amount: number;
    description: string;
  }) => Promise<void>;
  isLoading?: boolean;
}

export default function ExtraPaymentModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: ExtraPaymentModalProps) {
  const [selectedResource, setSelectedResource] = useState<string>("");
  const [selectedResourceType, setSelectedResourceType] = useState<"individual" | "subs" | null>(null);
  const [amount, setAmount] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [resources, setResources] = useState<{
    individuals: AssignableResource[];
    subs: AssignableResource[];
  }>({ individuals: [], subs: [] });
  const [loadingResources, setLoadingResources] = useState(false);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load assignable resources when modal opens
  useEffect(() => {
    if (isOpen) {
      loadResources();
    } else {
      // Reset when modal closes
      setSelectedResource("");
      setSelectedResourceType(null);
      setAmount("");
      setDescription("");
    }
  }, [isOpen]);

  const loadResources = async () => {
    setLoadingResources(true);
    try {
      const data = await IndividualsRepository.listAssignableResources();
      setResources({
        individuals: data.individuals.map((emp) => ({ ...emp, type: "individual" as const })),
        subs: data.subs.map((sub) => ({ ...sub, type: "sub" as const })),
      });
    } catch (error) {
      console.error("Failed to load assignable resources:", error);
      setResources({ individuals: [], subs: [] });
    } finally {
      setLoadingResources(false);
    }
  };

  const handleResourceChange = (value: string) => {
    const newValue = value === selectedResource ? "" : value;
    setSelectedResource(newValue);
    // Determine resource type based on selected value
    const isIndividual = resources.individuals.some((emp) => emp.id === newValue);
    const isSub = resources.subs.some((sub) => sub.id === newValue);
    if (isIndividual) {
      setSelectedResourceType("individual");
    } else if (isSub) {
      setSelectedResourceType("subs");
    } else {
      setSelectedResourceType(null);
    }
    setComboboxOpen(false);
  };

  const selectedResourceName = (() => {
    if (!selectedResource) return null;
    const emp = resources.individuals.find((e) => e.id === selectedResource);
    if (emp) return emp.name;
    const sub = resources.subs.find((s) => s.id === selectedResource);
    if (sub) return sub.name;
    return null;
  })();

  const handleClose = () => {
    setSelectedResource("");
    setSelectedResourceType(null);
    setAmount("");
    setDescription("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedResource || !selectedResourceType || !amount || !description.trim()) {
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      return;
    }

    try {
      await onConfirm({
        recipientType: selectedResourceType,
        recipientId: selectedResource,
        amount: amountNum,
        description: description.trim(),
      });
      handleClose();
    } catch (error) {
      console.error("Extra payment failed:", error);
      // Error handling is done in parent component
    }
  };

  const isFormValid =
    selectedResource &&
    selectedResourceType &&
    amount &&
    !isNaN(parseFloat(amount)) &&
    parseFloat(amount) > 0 &&
    description.trim().length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Send Extra Payment</DialogTitle>
          <DialogDescription>
            Send an extra payment to an individual or sub contractor via Stripe. This payment will be recorded separately
            from regular payment dues.
          </DialogDescription>
        </DialogHeader>

        <div ref={containerRef} className="space-y-4 py-4">
          {/* Resource Selection */}
          <div className="space-y-2">
            <Label htmlFor="resource">Recipient *</Label>
            {loadingResources ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">Loading resources...</span>
              </div>
            ) : (
              <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={comboboxOpen}
                    className="w-full justify-between font-normal"
                  >
                    {selectedResourceName ?? (
                      <span className="text-muted-foreground">Select individual or sub contractor</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" container={containerRef.current}>
                  <Command>
                    <CommandInput placeholder="Search..." />
                    <CommandList>
                      <CommandEmpty>No resources found.</CommandEmpty>

                      {/* Sub Contractors Group */}
                      {resources.subs.length > 0 && (
                        <CommandGroup heading="Sub Contractors">
                          {resources.subs.map((sub) => (
                            <CommandItem
                              key={sub.id}
                              value={sub.name}
                              onSelect={() => handleResourceChange(sub.id)}
                            >
                              <Check
                                className={cn("mr-2 h-4 w-4", selectedResource === sub.id ? "opacity-100" : "opacity-0")}
                              />
                              <Building2 className="mr-2 w-4 h-4" />
                              {sub.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}

                      {/* Separator between groups */}
                      {resources.subs.length > 0 && resources.individuals.length > 0 && <Separator />}

                      {/* Individual/Individuals Group */}
                      {resources.individuals.length > 0 && (
                        <CommandGroup heading="Individual/Individuals">
                          {resources.individuals.map((individual) => (
                            <CommandItem
                              key={individual.id}
                              value={individual.name}
                              onSelect={() => handleResourceChange(individual.id)}
                            >
                              <Check
                                className={cn("mr-2 h-4 w-4", selectedResource === individual.id ? "opacity-100" : "opacity-0")}
                              />
                              <User className="mr-2 w-4 h-4" />
                              {individual.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="amount">Amount (USD) *</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Description Input */}
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              placeholder="Enter payment description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={EXTRA_PAYMENT_DESCRIPTION_MAX_LENGTH}
              rows={4}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isFormValid || isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Send Payment"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
